import React, { type ComponentProps, useCallback, useEffect, useRef } from 'react';
import { AccessibilityInfo, findNodeHandle, Platform, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { GlassView } from '../../GlassView';
import { Icon, IconLink, type IconName } from '../../Icon';
import { HighlightWords } from '../../Text';

import type { NoticeBanner } from '../NoticeBanner';

// ─────────────────────────────────────────────────────────────────────────────

export const NoticeToast = (props: Props) => {
  const {
    text,
    highlights,
    ContentComponent,
    palette = 'neutral',
    withCloseBtn = true,
    autoFocusCloseBtn,
    closeBtnAccessibilityLabel = CLOSE_BTN_A11Y_LABEL,
    closeBtnAccessibilityHint = CLOSE_BTN_A11Y_HINT,
    style,
    isLiquidGlassEnabled = false,
    onClose,
  } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const closeBtnRef = useRef<View>(null);

  // ─── Derived ─────────────────────────────────────────────────────────

  const hasCustomContent = ContentComponent !== undefined;

  // ─── Handlers ────────────────────────────────────────────────────────

  const announceText = useCallback(() => {
    if (!text) {
      return;
    }

    AccessibilityInfo.announceForAccessibility(text);
  }, [text]);

  const focusCloseBtn = useCallback(() => {
    if (!autoFocusCloseBtn) {
      return;
    }

    focusElement(closeBtnRef.current);
  }, [autoFocusCloseBtn]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(announceText, [announceText]);
  useEffect(focusCloseBtn, [focusCloseBtn]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const surfaceStyles = isLiquidGlassEnabled
    ? [styles.containerGlass, glassPaletteStyles[palette]]
    : [styles.containerSolid, paletteStyles[palette]];
  const containerStyles = [styles.container, surfaceStyles, style];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      {isLiquidGlassEnabled ? (
        <GlassView
          glassEffectStyle="clear"
          tintColor={GLASS_TINT_COLORS[palette]}
          style={styles.glass}
        />
      ) : null}

      {hasCustomContent ? null : (
        <View style={styles.iconContainer} aria-hidden>
          <Icon
            name={PALETTE_ICONS[palette]}
            width={ICON_SIZE}
            height={ICON_SIZE}
            color={PALETTE_ICON_COLORS[palette]}
          />
        </View>
      )}

      <View style={styles.contentContainer}>
        {ContentComponent ? <ContentComponent /> : null}

        {text ? (
          <HighlightWords
            text={text}
            highlights={highlights}
            sizeMatch={['14']}
            accessibilityRole="alert"
          />
        ) : null}
      </View>

      {withCloseBtn ? (
        <View style={styles.closeBtnContainer}>
          <IconLink
            ref={closeBtnRef}
            name="IconBadgeClose"
            width={CLOSE_BTN_SIZE}
            height={CLOSE_BTN_SIZE}
            hitSlop={theme.spacing['3']}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={closeBtnAccessibilityLabel}
            accessibilityHint={closeBtnAccessibilityHint}
          />
        </View>
      ) : null}
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function focusElement(element: View | null) {
  if (!element) {
    return;
  }

  if (Platform.OS === 'web') {
    element.focus();

    return;
  }

  const node = findNodeHandle(element);

  if (!node) {
    return;
  }

  AccessibilityInfo.setAccessibilityFocus(node);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['3'],
    minHeight: theme.spacing['14'],
    paddingVertical: theme.spacing['3'],
    paddingLeft: theme.spacing['4'],
    paddingRight: theme.spacing['3'],
    borderRadius: theme.radius.large,
  },
  containerSolid: {
    borderWidth: 1,
    boxShadow: '0px 8px 32px 4px rgba(14, 21, 14, 0.1)',
  },
  containerGlass: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius.large,
    pointerEvents: 'none',
  },
  iconContainer: {
    flexShrink: 0,
  },
  contentContainer: {
    flex: 1,
  },
  closeBtnContainer: {
    flexShrink: 0,
  },
});

const paletteStyles = StyleSheet.create({
  neutral: {
    backgroundColor: theme.colors.LINEN,
    borderColor: theme.colors.LIGHT,
  },
  success: {
    backgroundColor: theme.colors.CUCUMBER,
    borderColor: theme.colors.AVOCADO,
  },
  caution: {
    backgroundColor: theme.colors.PEACH,
    borderColor: theme.colors.BLUSH,
  },
});

const glassPaletteStyles = StyleSheet.create({
  neutral: {
    backgroundColor: 'rgba(232, 232, 224, 0.85)',
  },
  success: {
    backgroundColor: 'rgba(216, 229, 214, 0.85)',
  },
  caution: {
    backgroundColor: 'rgba(249, 223, 206, 0.85)',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZE = 20;
const CLOSE_BTN_SIZE = 20;
const CLOSE_BTN_A11Y_LABEL = 'Close';
const CLOSE_BTN_A11Y_HINT = 'Closes notification';

const PALETTE_ICONS: Record<Palette, IconName> = {
  neutral: 'IconBadgeInfo',
  success: 'IconBadgeCheck',
  caution: 'IconBadgeAlert',
};

const GLASS_TINT_COLORS: Record<Palette, string> = {
  neutral: 'rgba(232, 232, 224, 0.85)',
  success: 'rgba(216, 229, 214, 0.85)',
  caution: 'rgba(249, 223, 206, 0.85)',
};

const PALETTE_ICON_COLORS: Record<Palette, string> = {
  neutral: theme.colors.CHARCOAL,
  success: theme.colors.KALE,
  caution: theme.colors.CAUTION,
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Pick<
  ComponentProps<typeof NoticeBanner>,
  | 'text'
  | 'highlights'
  | 'ContentComponent'
  | 'palette'
  | 'withCloseBtn'
  | 'autoFocusCloseBtn'
  | 'closeBtnAccessibilityLabel'
  | 'closeBtnAccessibilityHint'
  | 'style'
  | 'onClose'
> & {
  isLiquidGlassEnabled?: boolean;
};

type Palette = NonNullable<Props['palette']>;
